/** Refresh Cognito authentication before portal API requests leave the browser.
 * Redux retains a sign-in snapshot; Amplify owns refresh-token/session handling.
 * Never replay a request after a 403: it may be a real permission failure, and
 * mutations such as purchases or invitations must not be submitted twice.
 */
export function installSessionAuth(client, { apiUrl, stage, currentSession }) {
  if (!apiUrl || !stage) return () => {};
  const base = new URL(`${apiUrl.replace(/\/+$/, '')}/${stage}/`);
  let pending;

  function token() {
    // A burst of polls and form reads shares refresh work, not a cached JWT.
    if (!pending) {
      pending = Promise.resolve().then(currentSession).then((session) => {
        const value = session?.getAccessToken?.().getJwtToken?.();
        if (!value) throw new Error('Your session is unavailable. Please sign in again.');
        return value;
      }).finally(() => { pending = undefined; });
    }
    return pending;
  }

  const id = client.interceptors.request.use(async (config) => {
    let target;
    try {
      target = new URL(config.url, config.baseURL || base.href);
    } catch {
      return config;
    }
    // Never attach Cognito credentials to Meta, presigned S3 URLs or another stage.
    if (target.origin !== base.origin || !target.pathname.startsWith(base.pathname)) return config;
    const jwt = await token();
    if (typeof config.headers?.set === 'function') {
      config.headers.set('Authorization', `Bearer ${jwt}`);
    } else {
      const headers = { ...config.headers };
      Object.keys(headers).forEach((key) => {
        if (key.toLowerCase() === 'authorization') delete headers[key];
      });
      config.headers = { ...headers, Authorization: `Bearer ${jwt}` };
    }
    return config;
  });
  return () => client.interceptors.request.eject(id);
}
